/**
 * Font Family Hydration Utilities
 *
 * These utilities allow you to "hydrate" font families from third-party design systems
 * (like Metal Foundation) to use your application's CSS variables while preserving
 * the existing fallback chain.
 */

const CSS_FONT_VARIABLES = {
  inter: "var(--font-inter)",
  flecha: "var(--font-flecha)",
  bronzea: "var(--font-bronzea)",
  spaceMono: "var(--font-space-mono)",
} as const;

// Font family mappings for replacement
const FONT_FAMILY_REPLACEMENTS: Record<string, string> = {
  '"Inter Display"': CSS_FONT_VARIABLES.inter,
  "'Inter Display'": CSS_FONT_VARIABLES.inter,
  "Inter Display": CSS_FONT_VARIABLES.inter,
  '"Inter"': CSS_FONT_VARIABLES.inter,
  "'Inter'": CSS_FONT_VARIABLES.inter,
  Inter: CSS_FONT_VARIABLES.inter,
  '"system-ui"': CSS_FONT_VARIABLES.inter,
  "'system-ui'": CSS_FONT_VARIABLES.inter,
  "system-ui": CSS_FONT_VARIABLES.inter,
  '"Flecha"': CSS_FONT_VARIABLES.flecha,
  "'Flecha'": CSS_FONT_VARIABLES.flecha,
  Flecha: CSS_FONT_VARIABLES.flecha,
  '"Bronzea"': CSS_FONT_VARIABLES.bronzea,
  "'Bronzea'": CSS_FONT_VARIABLES.bronzea,
  Bronzea: CSS_FONT_VARIABLES.bronzea,
  '"Space Mono"': CSS_FONT_VARIABLES.spaceMono,
  "'Space Mono'": CSS_FONT_VARIABLES.spaceMono,
  "Space Mono": CSS_FONT_VARIABLES.spaceMono,
  SpaceMono: CSS_FONT_VARIABLES.spaceMono,
};

/**
 * Parse a font family string into individual font family units
 */
function parseFontFamilyString(fontFamily: string): string[] {
  const fonts: string[] = [];
  let current = "";
  let inQuotes = false;
  let quoteChar = "";

  for (let i = 0; i < fontFamily.length; i++) {
    const char = fontFamily[i];

    if ((char === '"' || char === "'") && !inQuotes) {
      // Start of quoted string
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (char === quoteChar && inQuotes) {
      // End of quoted string
      inQuotes = false;
      current += char;
      quoteChar = "";
    } else if (char === "," && !inQuotes) {
      // Comma outside quotes - end of font family
      if (current.trim()) {
        fonts.push(current.trim());
      }
      current = "";
    } else {
      current += char;
    }
  }

  // Add the last font family
  if (current.trim()) {
    fonts.push(current.trim());
  }

  return fonts;
}

/**
 * Hydrate a single font family string by replacing specific font names
 * while preserving the existing fallback chain
 */
export function hydrateFontFamily(fontFamily: string): string {
  const fontUnits = parseFontFamilyString(fontFamily);
  const hydratedUnits: string[] = [];
  const replacedFonts = new Set<string>();

  for (const unit of fontUnits) {
    const replacement = FONT_FAMILY_REPLACEMENTS[unit];
    if (replacement) {
      // Only add the replacement if we haven't already added it
      if (!replacedFonts.has(replacement)) {
        hydratedUnits.push(replacement);
        replacedFonts.add(replacement);
      }
      // Skip the original font since we replaced it
    } else {
      // Keep the original font
      hydratedUnits.push(unit);
    }
  }

  return hydratedUnits.join(", ");
}

/**
 * Recursively hydrates typography objects to use CSS variables for font families
 */
export function hydrateFontFamiliesInTypography(obj: any): any {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => hydrateFontFamiliesInTypography(item));
  }

  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === "fontFamily" && typeof value === "string") {
      result[key] = hydrateFontFamily(value);
    } else if (typeof value === "object") {
      result[key] = hydrateFontFamiliesInTypography(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Hydrates an entire theme object, replacing font families in typography
 */
export function hydrateFontFamiliesInTheme(theme: any): any {
  return {
    ...theme,
    typography: theme.typography
      ? hydrateFontFamiliesInTypography(theme.typography)
      : theme.typography,
  };
}
